import { ReactElement } from 'react';
import DropZone, { FileRejection } from 'react-dropzone';

import { FILE_SIZE_10MB } from 'modules/api/constants';
import { ACCEPTED_FILE_TYPE_XLS } from 'constants/fileType';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { useFileUpload } from 'customHooks/useFileUpload';
import FileUploadProps from './FileUpload.types';
import DisplayInputFileUpload from './DisplayInputFileUpload';
import DisplayCustomFileUpload from './DisplayCustomFileUpload';

const FileUpload = ({
  onFileChange: onFileChangeProp,
  hasReachedFileUploadLimit: hasReachedFileUploadLimitProp,
  acceptFileType = ACCEPTED_FILE_TYPE_XLS,
  maxFileSize = FILE_SIZE_10MB,
  isShowDropArea = false,
  placeholder = 'Try dropping some files here, or click to select files to upload.',
  isDisabled = false,
  isMultiple = false,
  isFileIconVisibleOnly,
  isFileUploadButton,
  theme = SectionButtonTheme.DASHED,
  maxFileUploads,
  uploadedFiles,
  setUploadedFiles,
  existingFiles,
  processFile,
  mapToFileContents,
  maxFileRows,
  onNotification,
  abortOnRejectedFiles,
}: FileUploadProps): ReactElement => {
  const { onFileChange: hookOnFileChange, hasReachedFileUploadLimit: hookLimit } = useFileUpload({
    uploadedFiles: uploadedFiles ?? [],
    processFile: processFile ?? (() => Promise.resolve([])),
    setUploadedFiles: setUploadedFiles ?? (() => {}),
    mapToFileContents: mapToFileContents ?? (() => {}),
    existingFiles,
    maxFileUploads,
    maxFileRows,
    onNotification,
    abortOnRejectedFiles,
  });

  const effectiveOnFileChange = onFileChangeProp ?? hookOnFileChange;
  const effectiveIsDisabled = isDisabled || (hasReachedFileUploadLimitProp ?? hookLimit);

  const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
    effectiveOnFileChange(acceptedFiles, rejectedFiles);
  };

  return (
    /* eslint-disable react/jsx-props-no-spreading */
    <DropZone
      onDrop={onDrop}
      accept={acceptFileType}
      maxSize={maxFileSize}
      disabled={effectiveIsDisabled}
      multiple={isMultiple}
      noClick={isFileIconVisibleOnly}
    >
      {({ getRootProps, getInputProps, isDragActive, open }) => (
        <div {...getRootProps()}>
          <input {...getInputProps()} data-test-id="file-upload-input" />
          {!isFileIconVisibleOnly && !isFileUploadButton ? (
            <DisplayInputFileUpload
              isDragActive={isDragActive}
              isShowDropArea={isShowDropArea}
              isEffectiveDisabled={effectiveIsDisabled}
              placeholder={placeholder}
            />
          ) : (
            <DisplayCustomFileUpload
              open={open}
              isFileIconVisibleOnly={isFileIconVisibleOnly}
              isEffectiveDisabled={effectiveIsDisabled}
              theme={theme}
              maxFileUploads={maxFileUploads}
            />
          )}
        </div>
      )}
    </DropZone>
    /* eslint-enable react/jsx-props-no-spreading */
  );
};

export default FileUpload;
